import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import { ALBUM_SECTIONS, isHiddenSticker, stickerCode } from '../albumData';
import { exportPdf } from '../utils/pdfExport';

const SCOPE_REPES   = 'repes';
const SCOPE_MISSING = 'missing';
const SCOPE_TRADE   = 'trade';

const FORMAT_CSV         = 'csv';
const FORMAT_TXT_INLINE  = 'txt-inline';
const FORMAT_TXT_GROUPED = 'txt-grouped';
const FORMAT_PDF_TABLE_X = 'pdf-table-x';
const FORMAT_PDF_TABLE_COLOR = 'pdf-table-color';
const FORMAT_PDF_TABLE_COLOR_CODE = 'pdf-table-color-code';
const FORMAT_PDF_PHOTO   = 'pdf-photo';

const PDF_FORMATS = new Set([FORMAT_PDF_TABLE_X, FORMAT_PDF_TABLE_COLOR, FORMAT_PDF_TABLE_COLOR_CODE, FORMAT_PDF_PHOTO]);

/**
 * Devuelve dos arrays con la data útil para exportar:
 *  - extras: una entrada por cada copia *tradeable* (reps - 1 entradas por sticker con reps>=2).
 *    Ej: si tenés 3 ARG7, aparece dos veces (las dos extras).
 *  - missing: entradas únicas de stickers que no tenés.
 */
function computeBuckets(stickers) {
  const extras = [];
  const missing = [];
  for (const s of stickers) {
    if (isHiddenSticker(s.n)) continue;
    if (!s.owned) {
      missing.push(s);
    } else if ((s.reps || 0) >= 2) {
      const extraCount = s.reps - 1;
      for (let i = 0; i < extraCount; i++) extras.push(s);
    }
  }
  return { extras, missing };
}

function joinByPrefix(items) {
  // [{n, ...}] -> Map<prefix, number[]>, conservando orden de ALBUM_SECTIONS.
  const byPrefix = new Map();
  for (const s of items) {
    const code = stickerCode(s.n);
    const m = code.match(/^([A-Z]+)(\d+)$/);
    if (!m) continue;
    const [, prefix, num] = m;
    if (!byPrefix.has(prefix)) byPrefix.set(prefix, []);
    byPrefix.get(prefix).push(num);
  }
  const order = [];
  for (const sec of ALBUM_SECTIONS) {
    if (sec.codePrefix) order.push(sec.codePrefix);
    if (sec.countries) order.push(...sec.countries);
  }
  const lines = [];
  for (const p of order) {
    if (byPrefix.has(p)) lines.push(`${p}: ${byPrefix.get(p).join(', ')}`);
  }
  return lines.join('\n');
}

function inlineList(items) {
  return items.map((s) => stickerCode(s.n)).join(', ');
}

function buildCSV(scope, extras, missing) {
  const header = 'n,code,owned,reps';
  const rows = [];
  if (scope === SCOPE_REPES || scope === SCOPE_TRADE) {
    // Para repes incluimos cada sticker con reps>=2 una sola vez (con su reps real).
    const seen = new Set();
    for (const s of extras) {
      if (seen.has(s.n)) continue;
      seen.add(s.n);
      rows.push(`${s.n},${stickerCode(s.n)},1,${s.reps}`);
    }
  }
  if (scope === SCOPE_MISSING || scope === SCOPE_TRADE) {
    for (const s of missing) {
      rows.push(`${s.n},${stickerCode(s.n)},0,0`);
    }
  }
  return [header, ...rows].join('\n');
}

function buildOutput(scope, format, extras, missing, t) {
  if (format === FORMAT_CSV) return buildCSV(scope, extras, missing);

  if (scope === SCOPE_REPES) {
    return format === FORMAT_TXT_INLINE ? inlineList(extras) : joinByPrefix(extras);
  }
  if (scope === SCOPE_MISSING) {
    return format === FORMAT_TXT_INLINE ? inlineList(missing) : joinByPrefix(missing);
  }
  // TRADE: ambos con headers.
  if (format === FORMAT_TXT_INLINE) {
    const a = extras.length ? `${t('Tengo (extras)')}: ${inlineList(extras)}` : '';
    const b = missing.length ? `${t('Me faltan')}: ${inlineList(missing)}` : '';
    return [a, b].filter(Boolean).join('\n');
  }
  // grouped
  const a = extras.length ? `${t('TENGO (extras)')}\n${joinByPrefix(extras)}` : '';
  const b = missing.length ? `${t('ME FALTAN')}\n${joinByPrefix(missing)}` : '';
  return [a, b].filter(Boolean).join('\n\n');
}

function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

function OptionCard({ icon, label, hint, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="w-full flex items-start gap-2 text-left rounded-xl transition-all"
      style={{
        backgroundColor: checked
          ? 'var(--md-sys-color-primary-container, rgba(37,99,235,0.10))'
          : 'var(--md-sys-color-surface, #ffffff)',
        border: `${checked ? 2 : 1}px solid ${checked ? 'var(--md-sys-color-primary, #2563eb)' : 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.12))'}`,
        padding: checked ? '8px 9px' : '9px 10px',
        boxShadow: checked ? '0 4px 12px -4px rgba(37,99,235,0.25)' : 'none',
      }}
    >
      <span
        className="material-symbols-outlined text-[20px] shrink-0 mt-0.5 transition-colors"
        style={{
          color: checked
            ? 'var(--md-sys-color-primary, #2563eb)'
            : 'var(--md-sys-color-on-surface-variant, #64748b)',
          fontVariationSettings: checked ? "'FILL' 1" : "'FILL' 0",
        }}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className="block text-[13px] font-bold leading-tight"
          style={{
            color: checked
              ? 'var(--md-sys-color-primary, #2563eb)'
              : 'var(--md-sys-color-on-surface, #111827)',
          }}
        >
          {label}
        </span>
        {hint && (
          <span className="block text-[10px] text-on-surface-variant mt-0.5 truncate">
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}

function ExportAlbumDialog({ open, stickers, displayName, onClose, onToast }) {
  const { t } = useTranslation();
  const [scope, setScope] = useState(SCOPE_TRADE);
  const [format, setFormat] = useState(FORMAT_PDF_TABLE_COLOR_CODE);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const buckets = useMemo(() => computeBuckets(stickers), [stickers]);
  const counts = {
    extras: buckets.extras.length,
    missing: buckets.missing.length,
  };

  const output = useMemo(
    () => buildOutput(scope, format, buckets.extras, buckets.missing, t),
    [scope, format, buckets, t]
  );

  const previewLines = useMemo(() => {
    if (!output) return [];
    return output.split('\n').slice(0, 4);
  }, [output]);

  const isPdf = PDF_FORMATS.has(format);
  const isEmpty = !output || output.trim().length === 0;

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      onToast?.({ message: t('Copiado al portapapeles'), variant: 'success' });
    } catch {
      onToast?.({ message: t('No se pudo copiar'), variant: 'error' });
    }
  };

  const handleDownload = () => {
    if (PDF_FORMATS.has(format)) {
      try {
        const variant = format === FORMAT_PDF_TABLE_X ? 'table-x'
                       : format === FORMAT_PDF_TABLE_COLOR ? 'table-color'
                       : format === FORMAT_PDF_TABLE_COLOR_CODE ? 'table-color-code'
                       : 'photo';
        exportPdf(variant, stickers, displayName);
        onToast?.({ message: t('Archivo descargado'), variant: 'success' });
      } catch (e) {
        onToast?.({ message: t('No se pudo generar el PDF'), variant: 'error' });
      }
      return;
    }
    const date = new Date().toISOString().slice(0, 10);
    const safeName = (displayName || 'album').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
    const ext = format === FORMAT_CSV ? 'csv' : 'txt';
    const mime = format === FORMAT_CSV ? 'text/csv' : 'text/plain';
    downloadBlob(output, `album_${safeName}_${date}.${ext}`, mime);
    onToast?.({ message: t('Archivo descargado'), variant: 'success' });
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: 'var(--md-sys-color-surface, #ffffff)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant/30">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]" style={{ color: C.primary }}>file_download</span>
            <h2 className="text-sm font-bold text-on-surface">{t('Exportar progreso')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-black/5"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-[18px] text-on-surface-variant">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <div className={`grid grid-cols-1 gap-3 ${isPdf ? '' : 'mobile-md:grid-cols-2'}`}>
            <div className={isPdf ? 'hidden' : ''}>
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1.5">
                {t('¿Qué incluir?')}
              </h3>
              <div className="space-y-1.5">
                <OptionCard
                  icon="inventory_2"
                  checked={scope === SCOPE_REPES}
                  onChange={() => setScope(SCOPE_REPES)}
                  label={t('Mis repetidas')}
                  hint={t('{{n}} extras', { n: counts.extras })}
                />
                <OptionCard
                  icon="playlist_remove"
                  checked={scope === SCOPE_MISSING}
                  onChange={() => setScope(SCOPE_MISSING)}
                  label={t('Las que me faltan')}
                  hint={t('{{n}} figuritas', { n: counts.missing })}
                />
                <OptionCard
                  icon="swap_horiz"
                  checked={scope === SCOPE_TRADE}
                  onChange={() => setScope(SCOPE_TRADE)}
                  label={t('Resumen para intercambios')}
                  hint={t('{{repes}} extras + {{missing}} faltantes', {
                    repes: counts.extras,
                    missing: counts.missing,
                  })}
                />
              </div>
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1.5">
                {t('Formato')}
              </h3>
              <div className="space-y-1.5">
                {/* Por ahora dejamos sólo "PDF · tabla con códigos". El resto
                    de formatos queda comentado para volver a iterar más adelante.

                <OptionCard
                  icon="format_align_left"
                  checked={format === FORMAT_TXT_INLINE}
                  onChange={() => setFormat(FORMAT_TXT_INLINE)}
                  label={t('TXT (lista separada por comas)')}
                  hint={t('Para WhatsApp o pegar rápido')}
                />
                <OptionCard
                  icon="public"
                  checked={format === FORMAT_TXT_GROUPED}
                  onChange={() => setFormat(FORMAT_TXT_GROUPED)}
                  label={t('TXT (agrupado por país)')}
                  hint={t('Una línea por país, mejor lectura')}
                />
                <OptionCard
                  icon="table_chart"
                  checked={format === FORMAT_CSV}
                  onChange={() => setFormat(FORMAT_CSV)}
                  label={t('CSV')}
                  hint={t('Para Excel / Sheets — n, code, owned, reps')}
                />
                <OptionCard
                  icon="picture_as_pdf"
                  checked={format === FORMAT_PDF_TABLE_X}
                  onChange={() => setFormat(FORMAT_PDF_TABLE_X)}
                  label={t('PDF · tabla con X')}
                  hint={t('X = la tengo, Xn = repetidas')}
                />
                <OptionCard
                  icon="palette"
                  checked={format === FORMAT_PDF_TABLE_COLOR}
                  onChange={() => setFormat(FORMAT_PDF_TABLE_COLOR)}
                  label={t('PDF · tabla con color')}
                  hint={t('Resaltado verde / ámbar')}
                />
                */}
                <OptionCard
                  icon="flag"
                  checked={format === FORMAT_PDF_TABLE_COLOR_CODE}
                  onChange={() => setFormat(FORMAT_PDF_TABLE_COLOR_CODE)}
                  label={t('PDF · tabla con códigos')}
                  hint={t('Banderitas + código completo (ARG3)')}
                />
                {/*
                <OptionCard
                  icon="grid_view"
                  checked={format === FORMAT_PDF_PHOTO}
                  onChange={() => setFormat(FORMAT_PDF_PHOTO)}
                  label={t('PDF · foto del álbum')}
                  hint={t('Cards por país en una página')}
                />
                */}
              </div>
            </div>
          </div>

          {!isPdf && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1">
                {t('Vista previa')}
              </h3>
              <pre
                className="text-[10px] font-mono whitespace-pre-wrap break-all rounded-lg p-2 max-h-[80px] overflow-y-auto"
                style={{
                  backgroundColor: 'var(--md-sys-color-surface-container, rgba(0,0,0,0.04))',
                  color: 'var(--md-sys-color-on-surface, #111827)',
                }}
              >
                {isEmpty
                  ? t('(sin contenido)')
                  : (previewLines.join('\n') + (output.split('\n').length > previewLines.length ? '\n…' : ''))}
              </pre>
            </div>
          )}
          {isPdf && (
            <div
              className="rounded-lg p-3 text-[11px] text-on-surface-variant flex items-start gap-2"
              style={{ backgroundColor: 'var(--md-sys-color-surface-container, rgba(0,0,0,0.04))' }}
            >
              <span className="material-symbols-outlined text-[16px] shrink-0 mt-0.5" style={{ color: C.primary }}>info</span>
              <span>{t('El PDF muestra tu álbum completo con el estado de cada figurita. Descargá para ver el resultado.')}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-outline-variant/30">
          {!isPdf && (
            <button
              type="button"
              onClick={handleCopy}
              disabled={isEmpty}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-40"
              style={{
                backgroundColor: 'var(--md-sys-color-surface-container, rgba(0,0,0,0.05))',
                color: 'var(--md-sys-color-on-surface, #111827)',
              }}
            >
              <span className="material-symbols-outlined text-base">content_copy</span>
              {t('Copiar')}
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={!isPdf && isEmpty}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-opacity disabled:opacity-40"
            style={{ backgroundColor: C.primary, color: '#fff' }}
          >
            <span className="material-symbols-outlined text-base">download</span>
            {t('Descargar archivo')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportAlbumDialog;
